import React, { useState, useEffect } from 'react';

import { useEscapeToClose } from '../hooks/useEscapeToClose';
const PERSONAL_MEDICAL_CONDITIONS = {
  "General": [
    "No known medical problems",
    "Chronic fatigue / chronic fatigue syndrome",
    "Fever of unknown origin (recurrent)",
    "Unexplained weight loss or gain",
    "Heat/cold intolerance"
  ],
  "Cardiovascular": [
    "High blood pressure (hypertension)",
    "High cholesterol / triglycerides (hyperlipidemia)",
    "Coronary artery disease (CAD) / angina",
    "Heart attack (myocardial infarction)",
    "Heart failure (CHF)",
    "Heart murmur / valvular disease (AS, MR, etc.)",
    "Atrial fibrillation / other arrhythmia",
    "Peripheral arterial disease (PAD)",
    "Carotid artery disease",
    "Aortic aneurysm (thoracic/abdominal)",
    "Congenital heart disease",
    "Blood clots (DVT) / pulmonary embolism (PE)",
    "Rheumatic fever / rheumatic heart disease",
    "Syncope (fainting) / presyncope",
    "Endocarditis / pericarditis history"
  ],
  "Endocrine & Metabolic": [
    "Diabetes (Type 1 or Type 2)",
    "Prediabetes / impaired glucose tolerance",
    "Gestational diabetes (for women)",
    "Thyroid disease (hypo/hyperthyroidism, nodules, cancer)",
    "Parathyroid disease / osteoporosis / low vitamin D",
    "Adrenal disorders (Addison's, Cushing's)",
    "Obesity / metabolic syndrome",
    "Gout / hyperuricemia",
    "Pituitary disorders"
  ],
  "Respiratory": [
    "Asthma",
    "Chronic obstructive pulmonary disease (COPD)",
    "Sleep apnea",
    "Pneumonia (recurrent)",
    "Pulmonary embolism (see Cardiovascular)",
    "Chronic cough / bronchitis",
    "Lung cancer / lung nodules",
    "Pleural effusion / pneumothorax",
    "Sarcoidosis / pulmonary fibrosis"
  ],
  "Gastrointestinal": [
    "Gastroesophageal reflux disease (GERD)",
    "Peptic ulcer disease / H. pylori",
    "Inflammatory bowel disease (Crohn's, ulcerative colitis)",
    "Irritable bowel syndrome (IBS)",
    "Celiac disease / gluten sensitivity",
    "Chronic constipation / diarrhea",
    "Gallbladder disease / gallstones",
    "Liver disease (fatty liver, hepatitis, cirrhosis)",
    "Pancreatic disease / pancreatitis",
    "Diverticulosis / diverticulitis",
    "Gastrointestinal bleeding (upper/lower)"
  ],
  "Genitourinary (Kidney & Urinary)": [
    "Chronic kidney disease (CKD) / kidney failure",
    "Kidney stones (nephrolithiasis)",
    "Frequent urinary tract infections (UTIs)",
    "Interstitial cystitis / bladder pain syndrome",
    "Urinary incontinence (stress/urge/mixed)",
    "Prostate enlargement (BPH)",
    "Prostatitis",
    "Elevated PSA / prostate cancer (history)"
  ],
  "Women's Health (if applicable)": [
    "Irregular or painful periods (dysmenorrhea)",
    "Heavy menstrual bleeding",
    "Endometriosis",
    "Polycystic ovary syndrome (PCOS)",
    "Uterine fibroids",
    "Ovarian cysts",
    "Infertility history",
    "Pregnancy history (G/P), miscarriages, ectopic pregnancy",
    "Preeclampsia / gestational hypertension",
    "Gestational diabetes (see above)",
    "Menopause / perimenopause symptoms",
    "Abnormal Pap smear / HPV",
    "Breast lumps / biopsies / breast cancer (history)"
  ],
  "Men's Health (if applicable)": [
    "Erectile dysfunction",
    "Low testosterone / hypogonadism",
    "Peyronie's disease",
    "Infertility history",
    "Testicular mass / pain / torsion history"
  ],
  "Neurologic": [
    "Stroke / transient ischemic attack (TIA)",
    "Seizure disorder / epilepsy",
    "Migraine / chronic headaches",
    "Peripheral neuropathy",
    "Tremor / essential tremor",
    "Parkinson's disease / parkinsonism",
    "Multiple sclerosis (MS)",
    "Dementia / memory loss / mild cognitive impairment",
    "Carpal tunnel syndrome",
    "Balance problems / frequent falls",
    "Neuromuscular disease (myasthenia gravis, ALS) — specify"
  ],
  "Psychiatric & Behavioral": [
    "Depression",
    "Anxiety / panic disorder",
    "Bipolar disorder",
    "Post-traumatic stress disorder (PTSD)",
    "Attention-deficit/hyperactivity disorder (ADHD)",
    "Obsessive—compulsive disorder (OCD)",
    "Eating disorder (anorexia, bulimia, binge-eating)",
    "Schizophrenia / schizoaffective disorder",
    "Autism spectrum (if relevant to adult care)",
    "Insomnia / other sleep disorder (non-OSA)"
  ],
  "Musculoskeletal & Pain": [
    "Osteoarthritis",
    "Rheumatoid arthritis",
    "Psoriatic arthritis",
    "Ankylosing spondylitis / axial spondyloarthritis",
    "Fibromyalgia / chronic widespread pain",
    "Chronic back or neck pain / sciatica",
    "Carpal/tarsal tunnel, tendonitis, bursitis",
    "Osteoporosis / fragility fractures (see Endocrine too)",
    "Gout (see Endocrine) / pseudogout",
    "Joint replacement (hip/knee/shoulder) — list side"
  ],
  "Dermatologic": [
    "Eczema (atopic dermatitis)",
    "Psoriasis",
    "Rosacea",
    "Chronic hives (urticaria) / angioedema",
    "Acne (moderate—severe) or cystic acne",
    "Skin infections (recurrent) / MRSA history",
    "Vitiligo",
    "Skin cancer (basal cell, squamous cell, melanoma) — specify type/date",
    "Precancerous lesions (actinic keratoses)"
  ],
  "Hematology & Oncology": [
    "Anemia (iron-deficiency, B12/folate, other)",
    "Thalassemia / sickle cell trait or disease",
    "Hemophilia / von Willebrand disease / bleeding tendency",
    "Clotting disorder (Factor V Leiden, prothrombin mutation, APS)",
    "Low platelets (ITP, other) / \"sticky platelets\"",
    "Blood transfusion reaction history",
    "Cancer (any type) — specify site, stage, treatment, dates",
    "Lymphoma / leukemia / myeloma"
  ],
  "Allergy, Immunology & Autoimmune": [
    "Seasonal/environmental allergies (allergic rhinitis)",
    "Asthma (see Respiratory)",
    "Eczema/atopic disease (see Dermatology)",
    "Food allergy — specify",
    "Drug allergy — specify medication & reaction",
    "Anaphylaxis (any cause)",
    "Autoimmune thyroid disease (Hashimoto's/Graves') — see Endocrine",
    "Rheumatoid arthritis / lupus (SLE) / Sjögren's",
    "Scleroderma / vasculitis",
    "Polymyalgia rheumatica / temporal arteritis (GCA)",
    "Celiac disease (see GI)",
    "Immunodeficiency (primary or acquired)",
    "Frequent or unusual infections"
  ],
  "Infectious Diseases & Sexual Health": [
    "COVID-19 (notable complications/long COVID)",
    "Influenza complications / pneumonia (recurrent)",
    "Mononucleosis (EBV) history",
    "Hepatitis A / B / C (see Liver)",
    "HIV",
    "Syphilis",
    "Gonorrhea / chlamydia",
    "Genital herpes (HSV)",
    "HPV / genital warts",
    "Pelvic inflammatory disease (PID)",
    "Recurrent shingles (zoster) / severe varicella",
    "Tuberculosis (latent/treated) — see Respiratory",
    "MRSA colonization/infections"
  ],
  "Ear, Nose, Throat (ENT)": [
    "Chronic sinusitis / nasal polyps",
    "Allergic rhinitis (see Allergy)",
    "Recurrent otitis media / externa",
    "Hearing loss / tinnitus",
    "Ménière's disease",
    "Hoarseness / vocal cord disorders",
    "TMJ disorder"
  ],
  "Ophthalmology": [
    "Glaucoma",
    "Cataracts",
    "Macular degeneration",
    "Diabetic retinopathy",
    "Dry eye / blepharitis",
    "Uveitis / iritis"
  ],
  "Other Conditions": [
    "Falls (recurrent) / gait instability",
    "Fractures (osteoporotic/fragility)",
    "Assistive device use (cane/walker) — specify",
    "Tobacco use (current / former) — cigarettes, cigars, vaping, smokeless",
    "Alcohol use disorder",
    "Opioid use disorder / chronic opioid therapy",
    "Stimulant, sedative, cannabis, or other substance use disorder",
    "Asbestos, silica, coal dust exposure",
    "Lead or heavy metal exposure",
    "Radiation exposure (medical/occupational)",
    "Pesticide/chemical exposure",
    "Travel-related illnesses (malaria, etc.) — specify"
  ]
};

const PersonalHistoryModal = ({ isOpen, onClose, onSave, existingData }) => {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [selectedConditions, setSelectedConditions] = useState({});
  const [diagnosisDates, setDiagnosisDates] = useState({});

  useEffect(() => {
    if (existingData) {
      // FIXED: Use snake_case field names that match backend
      setDateOfBirth(existingData.date_of_birth || '');
      setSelectedConditions(existingData.selected_conditions || {});
      setDiagnosisDates(existingData.diagnosis_dates || {});
    }
  }, [existingData]);

  const handleConditionToggle = (condition) => {
    setSelectedConditions(prev => ({
      ...prev,
      [condition]: !prev[condition]
    }));
  };

  const handleDiagnosisDateChange = (condition, date) => {
    setDiagnosisDates(prev => ({
      ...prev,
      [condition]: date
    }));
  };

  const handleSave = () => {
    const formData = {
      // FIXED: Send snake_case field names that backend expects
      date_of_birth: dateOfBirth,
      selected_conditions: selectedConditions,
      diagnosis_dates: diagnosisDates
    };
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content personal-history-modal">
        <div className="modal-header">
          <h2>Personal Medical History</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="date-of-birth-section">
            <label htmlFor="dateOfBirth">Date of Birth:</label>
            <input
              type="date"
              id="dateOfBirth"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="conditions-grid">
            <div className="grid-header">
              <div className="checkbox-column">Select</div>
              <div className="condition-column">Medical Condition</div>
              <div className="date-column">Diagnosis Date (est.)</div>
            </div>

            {Object.entries(PERSONAL_MEDICAL_CONDITIONS).map(([category, conditions]) => (
              <div key={category} className="category-section">
                <div className="category-header phm-categoryHeaderRow">
                  <div className="category-title">{category}</div>
                </div>
                {conditions.map((condition, index) => (
                  <div key={`${category}-${index}`} className="condition-row">
                    <div className="checkbox-cell">
                      <input
                        type="checkbox"
                        id={`condition-${category}-${index}`}
                        checked={selectedConditions[condition] || false}
                        onChange={() => handleConditionToggle(condition)}
                      />
                    </div>
                    <div className="condition-cell">
                      <label htmlFor={`condition-${category}-${index}`}>{condition}</label>
                    </div>
                    <div className="date-cell">
                      <input
                        type="date"
                        value={diagnosisDates[condition] || ''}
                        onChange={(e) => handleDiagnosisDateChange(condition, e.target.value)}
                        disabled={!selectedConditions[condition]}
                        className="diagnosis-date-input"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>Cancel</button>
          <button className="save-button" onClick={handleSave}>Save Personal History</button>
        </div>
      </div>
    </div>
  );
};

export default PersonalHistoryModal;